import isEqual from 'underscore/modules/isEqual';
import { featureCollection, point, lineString } from '@turf/helpers';
import { addPolylines, removePolylines } from './polylines';
import { addChevrons, removeChevrons } from './chevrons';
import { addMarkers, removeMarkers } from './markers';
import { addLayer, addLayerEvent } from '../layers/layer_helpers';
import { createLabelLayer } from '../layers/labels';
import { closestInterpPt } from '../../trail_planner/closest_on_line';
import { addOrUpdateGeojsonSource } from '../sources/geojson_helpers';
import { getDraggableId } from './draggable';
import { isCloseToEnds } from '../point_helpers';
import { END, NODE, PLUS, START } from 'utils/constants/MarkerIds';

const trailPlannerId = 'trailPlannerMap';
const routeNodesId = `${trailPlannerId}-nodes`;
const routeNodesDragId = getDraggableId(routeNodesId);
const routePolylinesId = `${trailPlannerId}-polylines`;
const routeChevronsId = `${trailPlannerId}-chevrons`;
const newRouteNodeId = 999999999;

const styleRouteNodes = (trailPlanner, messages, hasAdminFeatures = false) => {
  const lines = trailPlanner.routePolylines
  const nodes = trailPlanner.routeNodes;
  // get all points, in order, accross all polylines. remove consecutive repeating points and first/last point
  const allPoints = lines.reduce((acc, polyline) => acc.concat(polyline.points.slice(1)), []).slice(1, -1)
  return nodes.map((routeNode, i) => {
    const iconImage = getRouteNodeMarkerType(nodes, routeNode, i);
    const labelMessage = iconImage === 'map-marker-node' && messages.dragToChange;
    // no need to calculate position if first or last node
    const positionMessage = (i === nodes.length - 1 || i === 0) || !hasAdminFeatures 
      ? undefined 
      : getRouteNodePositionMessage(routeNode, lines, allPoints, messages);

    return {
      ...routeNode,
      iconImage,
      labelMessage,
      positionMessage 
    };
  });
};

// returns "Out Selected" or "Back Selected" message for markers on overlapping lines"
const getRouteNodePositionMessage = (node, lines, allPoints = [], messages) => {
  if(!lines.length || !allPoints.length) {
    // display no tooltip
    return undefined
  }

  // get polyline point closest to marker point
  const markerPoint = lines[node.idx]?.points[1];

  // find all instances of the point above
  const instancesOfPoint = allPoints.filter((point) => point[0] === markerPoint[0] && point[1] === markerPoint[1])

  // if there is more than one instance, we know the marker represents a spot where the route overlaps
  if (instancesOfPoint.length > 1) {
    // find number of points before and after the marker, to decide if the marker is on the out or back section of an overlap
    const beforePointCount = lines.reduce((acc, polyline, i) => acc + (i < node.idx ? polyline.points.length : 0), 0)
    const afterPointCount = lines.reduce((acc, polyline, i) => acc + (i >= node.idx ? polyline.points.length : 0), 0)
    return beforePointCount > afterPointCount ? messages.backSelected : messages.outSelected;
  }
  // display no tooltip
  return undefined
};

const getRouteNodeMarkerType = (nodes, node, i) => {
  if (i === 0) {
    return 'map-marker-start';
  }
  if (i === nodes.length - 1) {
    if (isEqual(node.lngLat, nodes[0].lngLat)) {
      return 'map-marker-start-end';
    } else {
      return 'map-marker-end';
    }
  }
  return 'map-marker-node';
};

const addPolylineEvents = (map, trailPlanner, pointText) => {
  // Remove new route node on mouse-out
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = map.customStyle.cursor;
    addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
  };

  // Show new route node when mousing-over polylineGeojson
  // Note: Here we are reusing the routeNodesDragId source/layer for this node to reduce the need to add yet another layer
  const onMouseMove = e => {
    if (map.shiftKeyDown) {
      onMouseLeave();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    const polylineIdx = e.features[0].properties.idx;
    const polyline = trailPlanner.routePolylines[polylineIdx];
    const points = polyline.points;
    if (isCloseToEnds(map, e.lngLat, points, 16)) {
      addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
      return;
    }
    const closest = closestInterpPt(e.lngLat.toArray(), points);
    const closestLngLat = closest.interpolatedPoint ? closest.interpolatedPoint : points[closest.cropIndex];
    const newRouteNode = {
      id: newRouteNodeId,
      cropIdx: closest.cropIndex,
      polylineIdx,
      iconImage: 'map-marker-plus',
      addPointText: pointText
    };
    const newRouteNodeGeojson = point(closestLngLat, newRouteNode, { id: newRouteNode.id });
    addOrUpdateGeojsonSource(map, routeNodesDragId, newRouteNodeGeojson);
    if (!map.dragging) {
      addLayer(map, createLabelLayer(`${routeNodesDragId}-labels`, routeNodesDragId, 'addPointText', true));
    }
  };

  addLayerEvent(map, routePolylinesId, true, 'mousemove', onMouseMove, false);
  addLayerEvent(map, routePolylinesId, true, 'mouseleave', onMouseLeave, false);
};

const addNodeEvents = (map, trailPlanner) => {
  // Handle route node dragged to new location
  addLayerEvent(map, routeNodesId, true, 'draglayerend', e => {
    const routeNode = e.draggingFeature.properties;
    trailPlanner.routeNodeMoved(routeNode, e.lngLat.toArray());
  });
};

const addTrailPlannerOverlay = (map, trailPlanner, lineColor, messages, hasAdminFeatures = false) => {
  // Draw route polylines
  const color = trailPlanner.color;
  const lineStrings = trailPlanner.routePolylines.map((routePolyline, idx) => lineString(routePolyline.points, { idx, color }));
  const polylineGeojson = featureCollection(lineStrings);
  const polylineLayerExists = map.getLayer(routePolylinesId);
  const polylineOpts = lineColor ? { 'line-color': lineColor } : null;
  addPolylines(map, routePolylinesId, polylineGeojson, {} /* sourceOpts */, polylineOpts);
  addChevrons(map, routeChevronsId, routePolylinesId);
  if (!polylineLayerExists) {
    addPolylineEvents(map, trailPlanner, messages.clickToAddPoint);
  }

  // Draw route nodes
  const routeNodes = styleRouteNodes(trailPlanner, messages, hasAdminFeatures);
  const routeLayerExists = map.getLayer(routeNodesId);
  addMarkers(map, routeNodesId, routeNodes, [START, END, NODE, PLUS], null, null, true, hasAdminFeatures);
  if (!routeLayerExists) {
    addNodeEvents(map, trailPlanner);
  }
};

const removeTrailPlannerMap = map => {
  removeMarkers(map, routeNodesId, true);
  removeChevrons(map, routeChevronsId);
  removePolylines(map, routePolylinesId);
};

export { addTrailPlannerOverlay, removeTrailPlannerMap, routeNodesId, routeNodesDragId, newRouteNodeId };
